import * as Actions from './AdminSessionsActions';
import {
  normalizeSessions,
  computeSessionStats,
  getUpdatedStatsOnToggleAllSessionKeys,
  getUpdatedStatsOnToggleSessionKey,
  getCurrentSession
} from '../Sessions/SessionsUtils';
import { MAX_RANGE, MIN_RANGE, SESSION_PAGE_COUNT } from '../../Utils/Constants';
import { getDifference, getUnion, sortArrayBasedOnArray } from '../../Utils/ArrayUtils';

const initialState = {
  fetching: false,
  filtering: false,
  sessionsFetching: false,
  trendFetching: null,
  creatorsFetching: false,
  sessions: {
    byKey: {},
    allKeys: [],
    deSelectedKeys: [],
    filteredKeys: [],
    filteredSelectedSessionKeys: []
  },
  filterReportSessions: {
    byKey: {},
    allKeys: [],
    deSelectedKeys: [],
    filteredKeys: [],
    filteredSelectedSessionKeys: []
  },
  uniqueTableId: '',
  filter: {
    fromTime: null,
    toTime: null,
    searchString: '',
    creators: [],
    registrantRange: [MIN_RANGE, MAX_RANGE],
    attendeeRange: [MIN_RANGE, MAX_RANGE]
  },
  stats: {
    filteredTotalRegistrants: 0,
    filteredTotalAttendees: 0,
    filteredTotalQAndACount: 0,
    filteredTotalEngagementCount: 0,
    filteredSelectedTotalRegistrants: 0,
    filteredSelectedTotalAttendees: 0,
    filteredSelectedTotalQAndACount: 0,
    filteredSelectedTotalEngagementCount: 0
  },
  filterSessionStats: {
    filteredTotalRegistrants: 0,
    filteredTotalAttendees: 0,
    filteredTotalQAndACount: 0,
    filteredTotalEngagementCount: 0,
    filteredSelectedTotalRegistrants: 0,
    filteredSelectedTotalAttendees: 0,
    filteredSelectedTotalQAndACount: 0,
    filteredSelectedTotalEngagementCount: 0
  },
  trend: {
    totalRegistrants: 0,
    totalAttendees: 0,
    prevDateRange: {
      previousFromTime: null,
      previousToTime: null
    }
  },
  pagination: {
    total: 0,
    limit: 0,
    page: 0,
    pageSize: SESSION_PAGE_COUNT
  },
  creators: {
    nextPage: null,
    page: 0,
    creators: [],
    filteredCreators: []
  },
  error: null
};

export default (state = initialState, action = {}) => {
  switch (action.type) {
    case Actions.POST_UNIQUE_ADMIN_TABLE_ID: {
      const { uniqueTableId } = action.payload;
      return {
        ...state,
        uniqueTableId: uniqueTableId
      };
    }

    case Actions.GET_ADMIN_SESSIONS_REQUEST: {
      const { fromTime, toTime, creators, registrantRange, attendeeRange } = action.payload;
      return {
        ...state,
        filter: {
          ...state.filter,
          fromTime: fromTime,
          toTime: toTime,
          creators: creators,
          registrantRange: registrantRange,
          attendeeRange: attendeeRange
        },
        fetching: action.payload.loadPage,
        sessionsFetching: !action.payload.loadPage,
        error: null
      };
    }

    case Actions.GET_ADMIN_SESSIONS_SUCCESS: {
      const { limit, sessions = [] } = action.payload;
      const { allKeys, byKey } = normalizeSessions(sessions);
      let { fromTime, toTime, creators } = state.filter;
      let { pagination } = action.payload;
      if (limit && limit === 10) {
        const { 0: lastSession = {}, [sessions.length - 1]: firstSession = {} } = sessions;
        fromTime = firstSession.startTime ? firstSession.startTime : state.filter.fromTime;
        toTime = lastSession.startTime ? lastSession.startTime : state.filter.toTime;
        pagination = state.pagination;
      }

      let sessionData = state.sessions;
      const newFilterSelectedSessionKeys = getDifference(allKeys, sessionData.deSelectedKeys);
      const computedStats = computeSessionStats(byKey, newFilterSelectedSessionKeys);

      let filterSession = state.filterReportSessions;
      const newFilterDeSelectedSessionKeys = getDifference(allKeys, filterSession.filteredSelectedSessionKeys);
      const newFilteredSelectedSessionKeysSecond = getDifference(allKeys, newFilterDeSelectedSessionKeys);
      const computedStatsSecond = computeSessionStats(byKey, newFilterDeSelectedSessionKeys);

      return {
        ...state,
        fetching: false,
        sessionsFetching: false,
        trendFetching: true,
        sessions: {
          ...state.sessions,
          byKey,
          allKeys,
          filteredKeys: allKeys,
          filteredSelectedSessionKeys: newFilterSelectedSessionKeys
        },
        filterReportSessions: {
          ...state.filterReportSessions,
          byKey,
          allKeys,
          filteredKeys: allKeys,
          deSelectedKeys: newFilterDeSelectedSessionKeys,
          filteredSelectedSessionKeys: newFilteredSelectedSessionKeysSecond
        },
        filter: {
          ...state.filter,
          fromTime: fromTime,
          toTime: toTime,
          creators: creators
        },
        filterSessionStats: {
          ...state.filterSessionStats,
          filteredTotalRegistrants: computedStatsSecond.totalRegistrants,
          filteredTotalAttendees: computedStatsSecond.totalAttendees,
          filteredTotalQAndACount: computedStatsSecond.totalQAndACount,
          filteredTotalEngagementCount: computedStatsSecond.totalEngagementCount
        },
        stats: {
          ...state.stats,
          ...{
            filteredTotalRegistrants: computedStats.totalRegistrants,
            filteredTotalAttendees: computedStats.totalAttendees,
            filteredTotalQAndACount: computedStats.totalQAndACount,
            filteredTotalEngagementCount: computedStats.totalEngagementCount,
            filteredSelectedTotalRegistrants: computedStats.totalRegistrants,
            filteredSelectedTotalAttendees: computedStats.totalAttendees,
            filteredSelectedTotalQAndACount: computedStats.totalQAndACount,
            filteredSelectedTotalEngagementCount: computedStats.totalEngagementCount
          }
        },
        pagination
      };
    }

    case Actions.GET_ADMIN_SESSIONS_FAILURE:
      return { ...state, ...initialState, fetching: false, sessionFetching: false, error: action.error };

    case Actions.GET_ADMIN_SESSIONS_TRENDS_REQUEST: {
      return {
        ...state,
        filter: {
          ...state.filter,
          fromTime: action.payload.fromTime,
          toTime: action.payload.toTime
        },
        trendFetching: true,
        error: null
      };
    }

    case Actions.GET_ADMIN_SESSIONS_TRENDS_SUCCESS: {
      const { totalRegistrants, totalAttendees, prevDateRange } = action.payload.trend;
      return {
        ...state,
        fetching: false,
        trendFetching: false,
        trend: {
          totalRegistrants: totalRegistrants,
          totalAttendees: totalAttendees,
          prevDateRange: {
            previousFromTime: prevDateRange.previousFromTime,
            previousToTime: prevDateRange.previousToTime
          }
        }
      };
    }

    case Actions.GET_ADMIN_SESSIONS_TRENDS_FAILURE:
      return { ...state, trendFetching: false, error: action.error };

    case Actions.TOGGLE_ADMIN_SESSION: {
      const { sessionKey, isSelected } = action.payload;

      const computedSession = getCurrentSession(state);
      let data = computedSession.currentSession;
      let filterReportFlag = computedSession.inGenerateReport;

      const { deSelectedKeys: existingDeSelectedKeys, allKeys } = data;
      const newDeSelectedKeys = isSelected
        ? existingDeSelectedKeys.filter(key => key !== sessionKey)
        : sortArrayBasedOnArray([...existingDeSelectedKeys, sessionKey], allKeys);
      const newFilterSelectedSessionKeys = getDifference(allKeys, newDeSelectedKeys);

      return {
        ...state,
        sessions: {
          ...state.sessions,
          deSelectedKeys: filterReportFlag ? state.sessions.deSelectedKeys : newDeSelectedKeys,
          filteredSelectedSessionKeys: filterReportFlag
            ? state.sessions.filteredSelectedSessionKeys
            : newFilterSelectedSessionKeys
        },
        filterReportSessions: {
          ...state.filterReportSessions,
          deSelectedKeys: filterReportFlag ? newDeSelectedKeys : state.filterReportSessions.deSelectedKeys,
          filteredSelectedSessionKeys: filterReportFlag
            ? newFilterSelectedSessionKeys
            : state.filterReportSessions.filteredSelectedSessionKeys
        },
        filterSessionStats: {
          ...state.filterSessionStats,
          ...(filterReportFlag
            ? getUpdatedStatsOnToggleSessionKey(state.filterSessionStats, isSelected, data.byKey[sessionKey])
            : state.filterSessionStats)
        },
        stats: {
          ...state.stats,
          ...(filterReportFlag
            ? state.stats
            : getUpdatedStatsOnToggleSessionKey(state.stats, isSelected, data.byKey[sessionKey]))
        }
      };
    }

    case Actions.TOGGLE_ALL_ADMIN_SESSIONS: {
      const computedSession = getCurrentSession(state);
      let data = computedSession.currentSession;
      let filterReportFlag = computedSession.inGenerateReport;

      const { deSelectedKeys: existingDeSelectedKeys, allKeys } = data;
      const newDeSelectedKeys = action.payload.isSelected
        ? getDifference(existingDeSelectedKeys, allKeys)
        : getUnion(existingDeSelectedKeys, allKeys);
      const newFilterSelectedSessionKeys = getDifference(allKeys, newDeSelectedKeys);
      return {
        ...state,
        sessions: {
          ...state.sessions,
          deSelectedKeys: filterReportFlag ? state.sessions.deSelectedKeys : newDeSelectedKeys,
          filteredSelectedSessionKeys: filterReportFlag
            ? state.sessions.filteredSelectedSessionKeys
            : newFilterSelectedSessionKeys
        },
        filterReportSessions: {
          ...state.filterReportSessions,
          deSelectedKeys: filterReportFlag ? newDeSelectedKeys : state.filterReportSessions.deSelectedKeys,
          filteredSelectedSessionKeys: filterReportFlag
            ? newFilterSelectedSessionKeys
            : state.filterReportSessions.filteredSelectedSessionKeys
        },
        filterSessionStats: {
          ...state.filterSessionStats,
          ...(filterReportFlag
            ? getUpdatedStatsOnToggleAllSessionKeys(state.filterSessionStats, action.payload.isSelected)
            : state.filterSessionStats)
        },
        stats: {
          ...state.stats,
          ...(filterReportFlag
            ? state.stats
            : getUpdatedStatsOnToggleAllSessionKeys(state.stats, action.payload.isSelected))
        }
      };
    }
    case Actions.GET_SESSION_CREATORS_REQUEST: {
      return {
        ...state,
        creatorsFetching: true,
        creators: action.payload.loadCreators
          ? { ...initialState.creators, filteredCreators: state.creators.filteredCreators }
          : state.creators
      };
    }
    case Actions.GET_SESSION_CREATORS_SUCCESS: {
      const creators = [...state.creators.creators, ...action.payload.creators];
      return {
        ...state,
        creatorsFetching: false,
        creators: {
          ...state.creators,
          page: action.payload.page,
          nextPage: action.payload.nextPage,
          filteredCreators: action.payload.filter ? [...state.creators.filteredCreators] : creators,
          creators: creators
        }
      };
    }
    case Actions.GET_SESSION_CREATORS_FAILURE:
      return { ...state, ...initialState, creatorsFetching: false, error: action.error };
    case Actions.RESET_SESSION_CREATORS_REQUEST:
      return {
        ...state,
        creators: {
          ...state.creators,
          creators: [...state.creators.filteredCreators]
        }
      };
    default:
      return state;
  }
};



// WEBPACK FOOTER //
// ./src/Reducks/AdminSessions/AdminSessionsReducer.js